import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import StatusBadge from '../../../Components/StatusBadge';
import NoDataPage from '../../../Components/NoDataPage';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, Body } from '../../../Constants/Fonts';
import {
    risk_portal_roles_list,
    risk_portal_role_update,
    risk_portal_role_delete,
    risk_portal_role_resend_invite,
} from '../../../services';
import InviteUser, { ROLE_OPTIONS } from '../Forms/InviteUser';

// Unwrap the SbomApiIntercepter / AIGApi envelope (same helper the other
// RiskPortal1 pages use).
const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const USER_ROLE_HEADERS = ['Name', 'Email', 'Role', 'Status', 'Actions'];

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// UserManagement — RBAC "User Roles" screen. Owns the data + mutations (invite,
// role change, activate/deactivate, resend, remove) and renders the roles table
// inline (inline role picker, status badge, and the row actions collapsed into a
// ⋮ menu via CommonActionMenu). Only risk_admin can mutate; others see it
// read-only. APIs mirror the reference RiskPortal.js user-management view.
export default function UserManagement({ role = 'risk_admin' }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showInvite, setShowInvite] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();
    const canManage = role === 'risk_admin';

    const load = useCallback(async () => {
        try {
            const d = unwrap(await risk_portal_roles_list());
            setUsers(Array.isArray(d?.roles) ? d.roles : Array.isArray(d?.users) ? d.users : []);
        } catch (e) {
            notify('Failed to load users', 'error');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    // Run a mutation, toast the result, and refetch.
    const op = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            if (okMsg) notify(okMsg, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const changeRole = (u, newRole) => op(() => risk_portal_role_update(u.id, { role: newRole }), 'Role updated');

    const toggleStatus = (u) => {
        const active = u.is_active !== false && u.status === 'active';
        return op(
            () => risk_portal_role_update(u.id, { is_active: !active, status: !active ? 'active' : 'inactive' }),
            !active ? 'User activated' : 'User deactivated',
        );
    };

    const resend = (u) => op(() => risk_portal_role_resend_invite(u.id), `Invite re-sent to ${u.email}`);

    const remove = async (u) => {
        const ok = await confirm({
            title: 'Remove user',
            message: `Remove ${u.email} from Risk Registry roles?`,
            confirmLabel: 'Remove', danger: true, themeColor: false,
        });
        if (!ok) return;
        op(() => risk_portal_role_delete(u.id), 'Role removed');
    };

    const rows = users.map((u) => ({
        data: [
            <Body as="span" variant={2} color="var(--color-text-primary)">{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{u.email || '—'}</Body>,
            canManage
                ? <CommonDropdown value={u.role} options={ROLE_OPTIONS} minWidth={160} ariaLabel="Role" onChange={(v) => changeRole(u, v)} />
                : <Body as="span" variant={2} color="var(--color-text-secondary)">{titleCase(u.role)}</Body>,
            <StatusBadge status={u.status} />,
            canManage ? (
                // 3 actions → CommonActionMenu collapses them into a ⋮ More menu.
                <CommonActionMenu
                    actions={[
                        { label: 'Resend Invite', onClick: () => resend(u), disabled: u.is_active === false, title: u.is_active === false ? 'Activate user first' : '' },
                        { label: u.status === 'active' ? 'Deactivate' : 'Activate', onClick: () => toggleStatus(u) },
                        { label: 'Remove', onClick: () => remove(u), danger: true },
                    ]}
                />
            ) : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
        ],
    }));

    if (loading) return <CommonLoader fullPage label="Loading users…" />;

    return (
        <div className="rp1_widget rp1_user_roles">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
                <h2 style={heading3({ margin: 0 })}>User Roles</h2>
                {canManage && <CommonButton text="Invite User" onClick={() => setShowInvite(true)} />}
            </div>

            {!canManage && (
                <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '0 0 14px' })}>
                    Only <strong>Risk Admin</strong> can modify roles — you're viewing in read-only mode.
                </p>
            )}

            {users.length === 0 ? (
                <NoDataPage hint="No users assigned. Click Invite User to add one." />
            ) : (
                <CommonTable headers={USER_ROLE_HEADERS} rows={rows} noPadding noScroll />
            )}

            <InviteUser
                show={showInvite}
                onClose={() => setShowInvite(false)}
                onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                onError={(msg) => notify(msg, 'error')}
            />

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
